import { useRef, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

export const GlobalCategoryDeleteAction = ({ category, reload }) => {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const triggerRef = useRef(null);
  const path = `/admin/pictogram-categories/${category.id}`;
  const message = (err, fallback) => typeof err.response?.data?.detail === "string" ? err.response.data.detail : fallback;
  const review = async () => {
    setLoading(true); setError("");
    try { setImpact((await api.get(`${path}/delete-impact`)).data); }
    catch (err) { setError(message(err, "Impossibile leggere l'impatto della cancellazione")); }
    finally { setLoading(false); }
  };
  const remove = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.delete(path, { params: { cascade: true, impact_token: impact.impact_token } });
      setImpact(null);
      toast.success("Categoria e pittogrammi eliminati");
      try { await reload(); }
      catch { toast.warning("Categoria eliminata. Ricarica la pagina per aggiornare l'elenco."); }
      setTimeout(() => document.querySelector('[data-testid="admin-picto-new-category"]')?.focus(), 0);
      if (data.file_cleanup_pending) toast.warning("Categoria eliminata; pulizia delle immagini in attesa.");
    } catch (err) {
      if (err.response?.status === 409) {
        try {
          setImpact((await api.get(`${path}/delete-impact`)).data);
          setError("L'impatto è cambiato: rileggi i numeri e conferma nuovamente.");
        } catch { setError("Impossibile aggiornare l'impatto. Riprova più tardi."); }
      } else setError(message(err, "Categoria non eliminabile"));
    } finally { setBusy(false); }
  };
  const close = () => { if (!busy) { setImpact(null); setError(""); setTimeout(() => triggerRef.current?.focus(), 0); } };

  return <>
    <button type="button" ref={triggerRef} title={loading ? "Calcolo impatto..." : "Elimina categoria"}
      aria-label={loading ? "Calcolo impatto..." : `Elimina ${category.name}`}
      aria-busy={loading} disabled={loading} onClick={review} className="btn-ghost !p-2"
      data-testid={`admin-picto-category-delete-${category.id}`}>
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
    </button>
    {error && !impact && <p role="alert" className="w-full text-sm text-[#8C3A3A]"
      data-testid={`admin-picto-category-preview-error-${category.id}`}>{error}</p>}
    <Dialog open={Boolean(impact)} onOpenChange={open => { if (!open) close(); }}>
      {impact && <DialogContent showCloseButton={false}
        className="!w-[calc(100vw-2rem)] !max-w-[calc(100vw-2rem)] sm:!max-w-lg min-w-0 grid-cols-[minmax(0,1fr)] [&>*]:min-w-0 max-h-[90vh] overflow-y-auto bg-white text-[#1A1A1A] p-6 rounded-sm"
        onCloseAutoFocus={event => { event.preventDefault();
          (triggerRef.current?.isConnected ? triggerRef.current : document.querySelector('[data-testid="admin-picto-new-category"]'))?.focus(); }}
        data-testid={`admin-picto-category-confirm-${category.id}`}>
        <DialogTitle className="font-serif text-2xl break-all" data-testid={`admin-picto-category-confirm-title-${category.id}`}>
          Eliminare «{impact.category_name}»?
        </DialogTitle>
        <DialogDescription className="text-sm text-[#525252] leading-relaxed"
          data-testid={`admin-picto-category-confirm-description-${category.id}`}>
          L'operazione è definitiva. Verranno eliminati {impact.pictogram_count}{" "}
          {impact.pictogram_count === 1 ? "pittogramma condiviso" : "pittogrammi condivisi"},
          anche se già usati da prodotti o sottocategorie.
        </DialogDescription>
        {impact.pictogram_count > 0 && <p className="text-sm break-all" data-testid={`admin-picto-category-affected-names-${category.id}`}>
          {impact.pictogram_names.join(" · ")}{impact.pictogram_count > impact.pictogram_names.length ? " · …" : ""}
        </p>}
        <ul className="text-sm leading-relaxed space-y-2 border-y border-[#E5E5E0] py-4">
          <li data-testid={`admin-picto-category-products-count-${category.id}`}>
            Rimossi da {impact.product_count} {impact.product_count === 1 ? "prodotto" : "prodotti"} e{" "}
            {impact.subcategory_count} {impact.subcategory_count === 1 ? "sottocategoria" : "sottocategorie"}; le schede restano intatte.
          </li>
          <li data-testid={`admin-picto-category-personal-count-${category.id}`}>
            {impact.personal_count} {impact.personal_count === 1 ? "pittogramma personale conservato" : "pittogrammi personali conservati"} senza categoria.
          </li>
          {(impact.customization_count > 0 || impact.override_reset_count > 0) &&
            <li data-testid={`admin-picto-category-overrides-count-${category.id}`}>
              {impact.customization_count} {impact.customization_count === 1 ? "personalizzazione rimossa" : "personalizzazioni rimosse"} dai pittogrammi eliminati;
              {" "}{impact.override_reset_count} {impact.override_reset_count === 1 ? "altra riportata" : "altre riportate"} alla categoria predefinita.
            </li>}
        </ul>
        {error && <p role="alert" className="text-sm text-[#8C3A3A]" data-testid={`admin-picto-category-delete-error-${category.id}`}>{error}</p>}
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-end">
          <button type="button" disabled={busy} onClick={close} className="btn-ghost w-full sm:w-auto"
            data-testid={`admin-picto-category-confirm-cancel-${category.id}`}>Annulla</button>
          <button type="button" disabled={busy} onClick={remove} className="btn-primary !bg-[#8C3A3A] min-w-0 w-full whitespace-normal break-words text-center leading-tight sm:w-auto"
            data-testid={`admin-picto-category-confirm-delete-${category.id}`}>
            {busy ? "Eliminazione..." : "Elimina categoria e pittogrammi"}
          </button>
        </div>
      </DialogContent>}
    </Dialog>
  </>;
};